import * as React from 'react'
import { styled } from 'next-yak'

import * as S from '../../styles'

import { SliderButton, type SliderButtonProps } from '../button/SliderButton'

export type PagedCarouselProps = React.HTMLAttributes<HTMLDivElement> & {
  style?: React.CSSProperties
  className?: string
  count?: number
  isPrevDisabled?: boolean
  isNextDisabled?: boolean
  fixedControls?: boolean
  hideControls?: boolean
  labels?: string[]
  onCountChange?: (newCount: number) => void
  sliderButtonSize?: SliderButtonProps['size']
}

function ControlledPagedCarousel({
  children,
  count,
  isPrevDisabled,
  isNextDisabled,
  fixedControls,
  hideControls,
  labels,
  onCountChange,
  sliderButtonSize = 'medium',
  ...rest
}: PagedCarouselProps & {
  count: number
  onCountChange: (newCount: number) => void
}): React.ReactElement<any> {
  const [animation, setAnimation] = React.useState('fade')

  const slides = React.Children.toArray(children)
  const slidesLength = slides.length

  const label = labels?.[count]

  React.useEffect(() => {
    if (onCountChange) {
      onCountChange(count)
    }
  }, [count, onCountChange])

  return (
    <S.carousel.PagedWrapper {...rest}>
      {!hideControls && (
        <S.carousel.PagedControlPrev fixedControls={fixedControls}>
          <SliderButton
            direction="left"
            size={sliderButtonSize}
            disabled={isPrevDisabled}
            type="button"
            onClick={() => {
              setAnimation('prev')
              count === 0 ? onCountChange(slidesLength - 1) : onCountChange(count - 1)
            }}
          />
        </S.carousel.PagedControlPrev>
      )}
      <S.carousel.PagedSlide data-animation={animation}>{slides[count]}</S.carousel.PagedSlide>
      {!hideControls && (
        <S.carousel.PagedControlNext fixedControls={fixedControls}>
          <SliderButton
            direction="right"
            size={sliderButtonSize}
            disabled={isNextDisabled}
            type="button"
            onClick={() => {
              setAnimation('next')
              count === slidesLength - 1 ? onCountChange(0) : onCountChange(count + 1)
            }}
          />
        </S.carousel.PagedControlNext>
      )}
      {(label || slidesLength > 1) && (
        <S.carousel.PagedLabelContainer>
          <S.carousel.PagedLabel>
            {label} {label && slidesLength > 1 && '-'} {slidesLength > 1 && `${count + 1}/${slidesLength}`}
          </S.carousel.PagedLabel>
        </S.carousel.PagedLabelContainer>
      )}
    </S.carousel.PagedWrapper>
  )
}

function UncontrolledPagedCarousel(props: PagedCarouselProps & { initialCount?: number }): React.ReactElement<any> {
  const [count, setCount] = React.useState(props.initialCount ?? 0)

  return <ControlledPagedCarousel count={count} onCountChange={setCount} {...props} />
}

function PagedCarouselComponent({ count, onCountChange, ...props }: PagedCarouselProps): React.ReactElement<any> {
  if (count !== undefined && onCountChange !== undefined) {
    return <ControlledPagedCarousel {...props} count={count} onCountChange={onCountChange} />
  } else {
    return <UncontrolledPagedCarousel {...props} />
  }
}

export const PagedCarousel = styled(PagedCarouselComponent)``
